<template>
  <div class="space-y-4">
    <!-- Growth Leaders -->
    <div v-if="growthLeaders.length > 0" class="space-y-3">
      <h4 class="text-sm font-semibold text-foreground flex items-center gap-2">
        {{ $t('insights.remarkable_growth') }}
      </h4>

      <div v-for="product in growthLeaders.slice(0, 3)" :key="product.CPRODUCT"
        class="p-3 rounded-xl insight-item">
        <div class="flex items-center justify-between gap-3">
          <div class="flex-1 min-w-0">
            <h5 class="font-medium text-foreground text-sm truncate">{{
              product.CDGN }}</h5>
            <p class="text-xs text-muted-foreground truncate">{{ product.CFAMILY_NAME }}
            </p>
          </div>

          <div class="shrink-0 text-right max-w-[45%]">
            <div class="text-xs font-bold text-green-600 truncate">{{
              formatCurrency(product.total_ati) }}</div>
            <div class="text-xs text-green-600 font-medium">+{{
              Math.round(product.growth_rate) }}%</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Market Leaders -->
    <div v-if="marketLeaders.length > 0" class="space-y-3">
      <h4 class="text-sm font-semibold text-foreground flex items-center gap-2">
        {{ $t('insights.market_leaders') }}
      </h4>

      <div v-for="product in marketLeaders.slice(0, 3)" :key="product.CPRODUCT"
        class="p-3 rounded-xl insight-item">
        <div class="flex items-center justify-between gap-3">
          <div class="flex-1 min-w-0">
            <h5 class="font-medium text-foreground text-sm truncate">{{
              product.CDGN }}</h5>
            <p class="text-xs text-muted-foreground truncate">{{ product.CFAMILY_NAME }}
            </p>
          </div>

          <div class="shrink-0 text-right max-w-[45%]">
            <div class="text-xs font-bold truncate link-cyan">{{
              formatCurrency(product.total_ati) }}</div>
            <div class="text-xs text-muted-foreground">{{ $t('insights.sales_count', { count: product.line_count }, product.line_count) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Analyse de concentration -->
    <div class="p-3 rounded-xl insight-item">
      <h4
        class="text-sm font-semibold text-foreground flex items-center gap-2 mb-2">
        {{ $t('insights.market_concentration') }}
      </h4>

      <div class="space-y-2">
        <div class="flex items-center justify-between text-sm">
          <span class="text-muted-foreground">{{ $t('insights.hhi_index') }}</span>
          <span class="font-medium text-foreground">{{ hhiIndex }}</span>
        </div>

        <div class="flex items-center justify-between text-sm">
          <span class="text-muted-foreground">{{ $t('insights.active_families') }}</span>
          <span class="font-medium text-foreground">{{ totalFamilies }}</span>
        </div>

        <div class="mt-3 p-2 bg-card rounded border-l-4"
          :class="concentrationBorderClass">
          <p class="text-xs" :class="concentrationTextClass">
            {{ concentrationAdvice }}
          </p>
        </div>
      </div>
    </div>

    <!-- Message si pas de données -->
    <div v-if="!hasData" class="text-center text-muted-foreground py-8">
      <p class="text-sm">{{ $t('insights.no_trend_data') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ProductsReportResponse } from '../responses'
import { formatCurrencyForCenter } from '@/utils/Languages';
import { Center } from '@/user';

interface Props {
  productsReport: ProductsReportResponse | null
  center: Center
}

const props = defineProps<Props>()
const { t } = useI18n()

const growthLeaders = computed(() => {
  return props.productsReport?.performance_analysis?.growth_leaders || []
})

const marketLeaders = computed(() => {
  return props.productsReport?.performance_analysis?.market_leaders || []
})

const hhiIndex = computed(() => {
  const hhi = props.productsReport?.performance_analysis?.diversity_metrics?.revenue_herfindahl_index || 0
  return Math.round(hhi)
})

const totalFamilies = computed(() => {
  return props.productsReport?.performance_analysis?.diversity_metrics?.total_families || 0
})

const hasData = computed(() => {
  return growthLeaders.value.length > 0 || marketLeaders.value.length > 0 || hhiIndex.value > 0
})

// Conseils selon l'indice HHI
const concentrationAdvice = computed(() => {
  const hhi = hhiIndex.value

  if (hhi <= 1500) return t('insights.hhi.excellent')
  if (hhi <= 2500) return t('insights.hhi.good')
  if (hhi <= 4000) return t('insights.hhi.moderate')
  if (hhi <= 6000) return t('insights.hhi.high')
  return t('insights.hhi.very_high')
})

const concentrationBorderClass = computed(() => {
  const hhi = hhiIndex.value
  if (hhi <= 1500) return 'border-green-400'
  if (hhi <= 2500) return 'border-blue-400'
  if (hhi <= 4000) return 'border-yellow-400'
  if (hhi <= 6000) return 'border-orange-400'
  return 'border-red-400'
})

const concentrationTextClass = computed(() => {
  const hhi = hhiIndex.value
  if (hhi <= 1500) return 'text-green-600'
  if (hhi <= 2500) return 'text-blue-600'
  if (hhi <= 4000) return 'text-yellow-600'
  if (hhi <= 6000) return 'text-orange-600'
  return 'text-red-600'
})

// Formatage monétaire
const formatCurrency = (amount: number): string => {
  return formatCurrencyForCenter(amount, props.center)
}
</script>

<style scoped>
.insight-item {
  border: 1px solid var(--border-medium);
  border-radius: var(--radius-card);
  transition: background 0.15s ease;
}

.insight-item:hover {
  background: rgba(0, 0, 0, 0.02);
}

:is(.dark *).insight-item:hover {
  background: rgba(255, 255, 255, 0.02);
}

.link-cyan {
  color: var(--apex-cyan);
}
</style>